import { JSX } from 'react';
import {
  GroupingCollapseState,
  MetamodelAssetFolder,
  MetamodelComponentType,
  MetamodelReferenceType,
  MetamodelWorkspace,
} from '@ardoq/api-types';
import {
  createAssetFolderNode,
  createComponentNode,
  createReferenceEdge,
  createWorkspaceNode,
} from './utils';
import { GraphComponent, LayoutOrientation } from '@ardoq/yfiles';
import {
  SimpleBlockDiagram,
  SimpleBlockDiagramContentType,
  SimpleBlockDiagramEdge,
  SimpleBlockDiagramLabels,
  SimpleBlockDiagramNode,
  SimpleBlockDiagramProps,
} from '@ardoq/simple-block-diagram';

type MetamodelVisualizationProps = {
  metamodelReferences: { [id: string]: MetamodelReferenceType };
  metamodelComponents: { [id: string]: MetamodelComponentType };
  metamodelWorkspaces: { [id: string]: MetamodelWorkspace };
  metamodelAssetFolders: { [id: string]: MetamodelAssetFolder };
  className?: string;
  loadingAsset?: JSX.Element | null;
  onItemFocus?: (metaData: {
    elementType: SimpleBlockDiagramContentType;
    elementId: string;
    referenceId?: string;
  }) => void;
  registerVisualizationContainer?: (container: HTMLElement) => void;
  unregisterVisualizationContainer?: () => void;
  registerGraphComponent?: (graphComponent: GraphComponent) => void;
  unregisterGraphComponent?: () => void;
  animateLayout?: boolean;
  hasZoomControls?: boolean;
  hasLeftMenu?: boolean;
  /**
   * Optional translated strings forwarded to the underlying
   * `SimpleBlockDiagram` (collapse/expand all controls and zoom tooltips).
   * Each label has an English default in the package, so passing `undefined`
   * (or omitting individual labels) preserves the previous English-only
   * behaviour.
   */
  labels?: Partial<SimpleBlockDiagramLabels>;
};

const mapMetamodelProps = ({
  metamodelReferences = {},
  metamodelComponents = {},
  metamodelWorkspaces = {},
  metamodelAssetFolders = {},
  className,
  loadingAsset,
  onItemFocus,
  registerVisualizationContainer,
  unregisterVisualizationContainer,
  registerGraphComponent,
  unregisterGraphComponent,
  animateLayout,
  hasZoomControls,
  hasLeftMenu,
  labels,
}: MetamodelVisualizationProps): SimpleBlockDiagramProps => {
  const componentNodes: SimpleBlockDiagramNode[] =
    Object.values(metamodelComponents).map(createComponentNode);

  const workspaceNodes: SimpleBlockDiagramNode[] =
    Object.values(metamodelWorkspaces).map(createWorkspaceNode);

  const assetFolderNodes: SimpleBlockDiagramNode[] = Object.values(
    metamodelAssetFolders
  ).map(createAssetFolderNode);

  const nodes = [...componentNodes, ...workspaceNodes, ...assetFolderNodes];

  const edges: SimpleBlockDiagramEdge[] =
    Object.values(metamodelReferences).map(createReferenceEdge);

  return {
    nodes,
    edges,
    className,
    loadingAsset,
    focusedItemId: nodes[0].id,
    onItemFocus,
    registerVisualizationContainer,
    unregisterVisualizationContainer,
    registerGraphComponent,
    unregisterGraphComponent,
    animateLayout,
    defaultGroupingCollapseState: GroupingCollapseState.EXPAND_ALL,
    layoutOrientation: LayoutOrientation.TOP_TO_BOTTOM,
    hasZoomControls,
    showLeftMenu: hasLeftMenu,
    labels,
  };
};

const MetamodelVisualization = (props: MetamodelVisualizationProps) => (
  <SimpleBlockDiagram {...mapMetamodelProps(props)} />
);

export { MetamodelVisualization };
